import { find } from 'lodash';
import { Box, Database, List, Gauge, Layers } from 'lucide-react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { useEnvironment } from '@/react/portainer/environments/queries';
import { useTags } from '@/portainer/tags/queries';
import { ContainerStatus } from '@/react/docker/DashboardView/ContainerStatus';
import { ImagesTotalSize } from '@/react/docker/DashboardView/ImagesTotalSize';
import { useDockerSnapshot } from '@/react/docker/queries/useDockerSnapshot';
import { filterUniqueContainersBasedOnStack } from '@/react/docker/snapshots/utils';
import { SnapshotStats } from '@/react/docker/DashboardView/EnvironmentInfo.SnapshotStats';
import { DockerSnapshot } from '@/react/docker/snapshots/types';
import { useIsEdgeAdmin } from '@/react/hooks/useUser';

import { Widget } from '@@/Widget';
import { DashboardGrid } from '@@/DashboardItem/DashboardGrid';
import { DashboardItem } from '@@/DashboardItem';
import { Link } from '@@/Link';

import { NoSnapshotAvailablePanel } from '../../components/NoSnapshotAvailablePanel';
import { EdgeDeviceViewsHeader } from '../../components/EdgeDeviceViewsHeader';

export function DashboardView() {
  const {
    params: { endpointId: environmentId },
  } = useCurrentStateAndParams();

  if (!environmentId) {
    throw new Error('Missing environmentId parameter');
  }

  const environmentQuery = useEnvironment(environmentId, undefined, {
    excludeSnapshot: false,
  });
  const snapshotQuery = useDockerSnapshot(environmentId);
  const tagsQuery = useTags();
  const edgeAdminQuery = useIsEdgeAdmin();

  const { data: environment } = environmentQuery;
  const { data: snapshot } = snapshotQuery;

  if (!environment || !tagsQuery.data) {
    return null;
  }

  const { data: tags } = tagsQuery;

  const tagsString = environment.TagIds.length
    ? environment.TagIds.map((id) => find(tags, { Id: id }))
        .filter(Boolean)
        .join(', ')
    : '-';

  const breadcrumbs = [
    { label: 'Edge Devices' },
    { label: environment.Name },
    { label: 'Dashboard' },
  ];

  if (!snapshot) {
    return (
      <>
        <EdgeDeviceViewsHeader
          title="Dashboard"
          breadcrumbs={breadcrumbs}
          environment={environment}
        />

        <NoSnapshotAvailablePanel />
      </>
    );
  }

  const { Containers: containers, Images: images, Volumes: volumes } = snapshot;

  const uniqueStacks = filterUniqueContainersBasedOnStack(containers);

  const imagesTotalSize = images.reduce((res, image) => res + image.Size, 0);

  return (
    <>
      <EdgeDeviceViewsHeader
        title="Dashboard"
        breadcrumbs={breadcrumbs}
        environment={environment}
      />

      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <Widget.Title icon={Gauge} title="Environment info" />
            <Widget.Body className="no-padding !px-5">
              <table className="table">
                <tbody>
                  <tr>
                    <td>Environment</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {environment.Name}
                        <SnapshotStats snapshot={environment.Snapshots[0]} />
                        <span className="small text-muted">
                          - Agent {environment.Agent.Version}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Tags</td>
                    <td>{tagsString}</td>
                  </tr>
                </tbody>
              </table>
            </Widget.Body>
          </Widget>
        </div>
      </div>

      <div className="mx-4">
        <DashboardGrid>
          {!edgeAdminQuery.isLoading && edgeAdminQuery.isAdmin && (
            <Link
              to="edge.browse.stacks"
              params={{ environmentId }}
              className="no-link"
              data-cy="edge-dashboard-stacks-link"
            >
              <DashboardItem
                icon={Layers}
                data-cy="edge-dashboard-stacks-link"
                type="Stack"
                value={uniqueStacks.length}
              />
            </Link>
          )}
          <Link
            to="edge.browse.containers"
            params={{ environmentId }}
            className="no-link"
            data-cy="edge-dashboard-containers-link"
          >
            <DashboardItem
              icon={Box}
              data-cy="edge-dashboard-containers-link"
              type="Container"
              value={containers.length}
            >
              <ContainerStatus
                stats={getContainerStats(environment.Snapshots[0])}
              />
            </DashboardItem>
          </Link>
          <Link
            to="edge.browse.images"
            params={{ environmentId }}
            className="no-link"
            data-cy="edge-dashboard-images-link"
          >
            <DashboardItem
              icon={List}
              data-cy="edge-dashboard-images-link"
              type="Image"
              value={images.length}
            >
              <ImagesTotalSize imagesTotalSize={imagesTotalSize} />
            </DashboardItem>
          </Link>
          <Link
            to="edge.browse.volumes"
            params={{ environmentId }}
            className="no-link"
            data-cy="edge-dashboard-volumes-link"
          >
            <DashboardItem
              icon={Database}
              data-cy="edge-dashboard-volumes-link"
              type="Volume"
              value={volumes.length}
            />
          </Link>
        </DashboardGrid>
      </div>
    </>
  );
}

function getContainerStats(snapshot: DockerSnapshot | undefined) {
  return {
    running: snapshot?.RunningContainerCount || 0,
    stopped: snapshot?.StoppedContainerCount || 0,
    healthy: snapshot?.HealthyContainerCount || 0,
    unhealthy: snapshot?.UnhealthyContainerCount || 0,
    total: snapshot?.ContainerCount || 0,
  };
}
